import { h } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { MapContainer, Marker as ReactMarker, useMap } from 'react-leaflet';
import { LatLngExpression, icon, Marker, divIcon, DivIcon, LatLngTuple, LatLng, Map as LeafletMap, maplibreGL } from 'leaflet';
import 'leaflet-doubletapdrag';
import 'leaflet-doubletapdragzoom';

import '@maplibre/maplibre-gl-leaflet'

import { IMapSettings } from '../app';

function OFMMapLayer() {
    
    const map = useMap();
    
    useEffect(() => {
        
        if (!map) return;
        
        const glLayer = maplibreGL({
            style: 'https://tiles.openfreemap.org/styles/fiord',
        });
        glLayer.addTo(map);
        
        return () => {
            map.removeLayer(glLayer);
        }
        
    }, [map]);
    
    return null;
    
}

const ICONS_STRIKE = [
    icon({
        iconUrl: (new URL('../../assets/tick_white.png', import.meta.url)).toString(),
        iconSize: [15, 15],
        iconAnchor: [4, 4],
        popupAnchor: [4, -4]
    }),
    icon({
        iconUrl: (new URL('../../assets/tick_yellow.png', import.meta.url)).toString(),
        iconSize: [15, 15],
        iconAnchor: [4, 4],
        popupAnchor: [4, -4]
    }),
    icon({
        iconUrl: (new URL('../../assets/tick_orange.png', import.meta.url)).toString(),
        iconSize: [15, 15],
        iconAnchor: [4, 4],
        popupAnchor: [4, -4]
    }),
    icon({
        iconUrl: (new URL('../../assets/tick_red_2.png', import.meta.url)).toString(),
        iconSize: [15, 15],
        iconAnchor: [4, 4],
        popupAnchor: [4, -4]
    }),
    icon({
        iconUrl: (new URL('../../assets/tick_darkred.png', import.meta.url)).toString(),
        iconSize: [15, 15],
        iconAnchor: [4, 4],
        popupAnchor: [4, -4]
    }),
    icon({
        iconUrl: (new URL('../../assets/tick_darkred2.png', import.meta.url)).toString(),
        iconSize: [15, 15],
        iconAnchor: [4, 4],
        popupAnchor: [4, -4]
    })
];

const ICONS_STRIKE_CLOUD = [
    icon({
        iconUrl: (new URL('../../assets/tick_white_cloud.png', import.meta.url)).toString(),
        iconSize: [15, 15],
        iconAnchor: [5, 5],
        popupAnchor: [5, -5]
    }),
    icon({
        iconUrl: (new URL('../../assets/tick_yellow_cloud.png', import.meta.url)).toString(),
        iconSize: [15, 15],
        iconAnchor: [5, 5],
        popupAnchor: [5, -5]
    }),
    icon({
        iconUrl: (new URL('../../assets/tick_orange_cloud.png', import.meta.url)).toString(),
        iconSize: [15, 15],
        iconAnchor: [5, 5], 
        popupAnchor: [5, -5]
    }),
    icon({
        iconUrl: (new URL('../../assets/tick_red_2_cloud.png', import.meta.url)).toString(),
        iconSize: [15, 15],
        iconAnchor: [5, 5],
        popupAnchor: [5, -5]
    }),
    icon({
        iconUrl: (new URL('../../assets/tick_darkred_cloud.png', import.meta.url)).toString(),
        iconSize: [15, 15],
        iconAnchor: [5, 5],
        popupAnchor: [5, -5]
    }),
    icon({
        iconUrl: (new URL('../../assets/tick_darkred2_cloud.png', import.meta.url)).toString(),
        iconSize: [15, 15],
        iconAnchor: [5, 5],
        popupAnchor: [5, -5]
    })
];

const ICON_GPS = divIcon({
    iconSize: [16, 16],
    className: 'x-gps-icon'
});

let hasLocationPermission = false;
let hasAskedLocationPermission = false;

const TAMPERE: LatLngExpression = [65.496634, 24.756104];

export type ILightningStrike = {
    timestamp: number,
    lat: number,
    lon: number,
    multiplicity: number,
    peakCurrent: number,
    cloudIndicator: number,
    ellipseMajor: number
};

export default function LightningMap(props: {
    settings: IMapSettings,
    onSubPanel: (to: string) => any
}) {
    
    const [screenHeight, setScreenHeight] = useState<number>(window.innerHeight);
    
    const [gpsLocation, setGpsLocation] = useState<[number, number]>([0, 0]);
    const [initialCenter, setInitialCenter] = useState<[number, number] | null>(null);
    const mapRef = useRef<LeafletMap>(null);
    
    const [strikeData, setStrikeData] = useState<ILightningStrike[]>([]);
    const [fetchIssues, setFetchIssues] = useState<boolean>(false);
    
    useEffect(() => {
        
        function update() {
            (async () => {
                
                const x = await fetch(`/api/strikes`);
                const raw = await x.json();
                
                if (raw.error || !raw.data) {
                    throw new Error(raw.error || `???`);
                }
                
                if (raw.hasFetchIssues) {
                    setFetchIssues(true);
                } else {
                    setFetchIssues(false);
                }
                
                const dataSorted = [...raw.data] as ILightningStrike[];
                dataSorted.sort((a, b) => a.timestamp - b.timestamp);
                
                setStrikeData(dataSorted);
                
            })()
                .catch(err => {
                    console.error(err);
                })
                .finally(() => {
                    to = setTimeout(update, 1000*10);
                })
        }
        
        let to = setTimeout(update, 1);
        
        return () => {
            clearTimeout(to);
        }
        
    }, []);
    
    useEffect(() => {
        
        let geolocationId: number | null = null;
        let hadInitial: boolean = false;
        
        if (navigator.geolocation) {
            geolocationId = navigator.geolocation.watchPosition(position => {
                
                hasLocationPermission = true;
                setGpsLocation([position.coords.latitude, position.coords.longitude]);
                
                if (!hadInitial) {
                    if (mapRef.current) {
                        mapRef.current.flyTo([position.coords.latitude, position.coords.longitude], 15);
                    }
                    hadInitial = true;
                }
                
            }, error => {
                console.error(error);
                hasLocationPermission = false;
            })
        }
        
        return () => {
            if (geolocationId != null) {
                navigator.geolocation.clearWatch(geolocationId);
            }
        }
        
    }, []);
    
    const cloudBolts = strikeData.filter(s => s.cloudIndicator);
    
    return <div className='x-floating-map'
        style={{  }}
        >
        <div className='x-floating-map-container'>
            <div className='x-map' style={{ }}>
                <MapContainer
                    center={TAMPERE}
                    zoom={5}
                    scrollWheelZoom={true}
                    markerZoomAnimation={false}
                    style={{ height: `${screenHeight}px` }}
                    {...{}/* @ts-ignore */}
                    doubleTapDragZoomOptions={{ reverse: true }}
                    ref={mapRef}
                    >
                        
                    
                    {/*<TileLayer
                        attribution='&copy; <a href="https://www.stadiamaps.com/" target="_blank">Stadia Maps</a> &copy; <a href="https://openmaptiles.org/" target="_blank">OpenMapTiles</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png"
                        minZoom={0}
                        maxZoom={20}
                        />*/}
                    <OFMMapLayer/>
                    
                    <ReactMarker position={[gpsLocation[0], gpsLocation[1]]} icon={ICON_GPS}/>
                    
                    {strikeData.map((strike, n) => {
                        
                        let icon = 0;
                        const strikeAge = Math.floor(Date.now()/1000) - strike.timestamp;
                        
                        if (strikeAge > 60*45) {
                            icon = 5;
                        } else if (strikeAge > 60*30) {
                            icon = 4;
                        } else if (strikeAge > 60*15) {
                            icon = 3;
                        } else if (strikeAge > 60*10) {
                            icon = 2;
                        } else if (strikeAge > 60*5) {
                            icon = 1;
                        }
                        
                        return <ReactMarker
                            key={`${strike.lat} ${strike.lon} ${strike.timestamp}`}
                            position={[ strike.lat, strike.lon ]}
                            icon={(strike.cloudIndicator ? ICONS_STRIKE_CLOUD[icon] : ICONS_STRIKE[icon])}
                            zIndexOffset={n}
                            />;
                        
                    })}
                    
                    <SizeBubbler onResize={s => setScreenHeight(s)}/>
                    
                </MapContainer>
            </div>
        </div>
        <aside className='x-topbar'>
            <div className='header'>
                <h1>Ukkoskartta<sup>☁️</sup></h1>
                <p className='copyright'>Data: <a href='https://www.ilmatieteenlaitos.fi/avoin-data' target='_blank'>FMI</a></p>
                <p className='copyright'>
                    <a href='#'
                        onClick={e => {
                            e.preventDefault();
                            props.onSubPanel('info');
                        }}>
                        🛈 Tietoa
                    </a>
                </p>
            </div>
            <div className='stats'>
                <h2>⚡️ {strikeData.length}</h2>
                <p>
                    <span className='stat'>🌩️ {cloudBolts.length}</span>
                    <span className='stat'>🌎️ {strikeData.length - cloudBolts.length}</span>
                </p>
            </div>
            {fetchIssues && <div className='issue'>
                Palvelimella vaikuttaa olevan häikkää datan noudossa. Tiedot eivät ole välttämättä ajantasaisia.
            </div>}
        </aside>
    </div>;
    
}

function SizeBubbler(props: { onResize: (n: number) => any }) {
    
    const [screenHeight, setScreenHeight] = useState<number>(window.innerHeight);
    
    const map = useMap();
    
    useEffect(() => {
        
        function onResize(e: any) {
            props.onResize(window.innerHeight);
            setScreenHeight(window.innerHeight);
            if (map) setTimeout(() => map.invalidateSize(), 1);
        }
        
        onResize(null);
        
        window.addEventListener('resize', onResize);
        
        return () => {
            window.removeEventListener('resize', onResize);
        }
        
    }, []);
    
    return <div></div>;
    
}


function encodeHTML(s: string) {
    return s.replace(/</gmi, '&lt;')
        .replace(/>/gmi, '&gt;')
        .replace(/"/gmi, '&quot;')
        .replace(/&/gmi, '&amp;');
}
